import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Flag, TrendingUp, Heart, Link, Loader } from 'lucide-react';

const LastSentenceAnalysis = ({ lastSentenceData, essayText }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // If no AI analysis data, show loading or nothing
  if (!lastSentenceData) {
    if (!essayText) return null;
    
    // Show loading state while waiting for AI analysis
    return (
      <div className="bg-violet-50 border border-violet-300 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 text-violet-600">
          <Loader className="w-4 h-4 animate-spin" />
          <span className="text-sm">Analyzing ending...</span>
        </div>
      </div>
    );
  }

  const getMemorabilityColor = (score) => {
    if (score >= 8) return 'text-emerald-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getClosureTypeIcon = (type) => {
    const icons = {
      'future-focused': '🚀',
      'reflection': '🪞',
      'callback': '🔄',
      'philosophical': '💭',
      'action': '⚡',
      'commitment': '🤝',
      'open-ended': '❓',
      'circular': '⭕',
      'emotional': '❤️'
    };
    return icons[type] || '📝';
  };

  const getClosureTypeDescription = (type) => {
    const descriptions = {
      'future-focused': 'Future-Focused - Looking ahead with purpose',
      'reflection': 'Reflective - Looking back with wisdom',
      'callback': 'Callback - Connects to opening brilliantly',
      'philosophical': 'Philosophical - Leaves reader thinking',
      'action': 'Action-Oriented - Ends with momentum',
      'commitment': 'Commitment - Makes concrete promises',
      'open-ended': 'Open-Ended - Invites continued thought',
      'circular': 'Circular - Returns to the beginning',
      'emotional': 'Emotional - Leaves reader feeling'
    };
    return descriptions[type] || 'Closing Style';
  };

  return (
    <div className="bg-violet-50 border border-violet-300 rounded-lg p-4 mb-6"
         style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'}}>
      <div className="flex items-start gap-2">
        <Flag className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-violet-800">Last Sentence Analysis</h3>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-violet-600 hover:text-violet-700 transition-colors flex items-center gap-1 text-xs"
            >
              {isExpanded ? (
                <>
                  <span>Hide Details</span>
                  <ChevronUp className="w-3 h-3" />
                </>
              ) : (
                <>
                  <span>Show Details</span>
                  <ChevronDown className="w-3 h-3" />
                </>
              )}
            </button>
          </div>

          {/* Ending Text */}
          <div className="mb-3 p-3 bg-white border border-violet-200 rounded">
            <p className="text-xs text-slate-800 font-medium italic">
              "{lastSentenceData.text}"
            </p>
            {lastSentenceData.contextualText && lastSentenceData.contextualText !== lastSentenceData.text && (
              <p className="text-xs text-slate-600 mt-2">
                <span className="font-medium">Context:</span> {lastSentenceData.contextualText}
              </p>
            )}
          </div>

          {/* Memorability Score */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Memorability:</span>
              <span className={`text-base font-bold ${getMemorabilityColor(lastSentenceData.memorability)}`}>
                {lastSentenceData.memorability}/10
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-slate-600">{getClosureTypeIcon(lastSentenceData.closureType)}</span>
              <span className="text-violet-700 font-medium">{getClosureTypeDescription(lastSentenceData.closureType)}</span>
            </div>
          </div>

          {/* Why It Works */}
          {lastSentenceData.whyItWorks && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 mb-3">
              <p className="text-xs text-emerald-800">
                <span className="font-semibold">Why it works: </span>
                {lastSentenceData.whyItWorks}
              </p>
            </div>
          )}

          {/* Callback to Opening */}
          {lastSentenceData.callbackToOpening && (
            <div className="bg-azure-50 border border-azure-200 rounded p-3 mb-3">
              <p className="text-xs text-azure-800">
                <span className="font-semibold">Connection to opening: </span>
                {lastSentenceData.callbackToOpening}
              </p>
            </div>
          )}

          {/* Emotional Resonance */}
          {lastSentenceData.emotionalResonance && (
            <div className="bg-violet-50 border border-violet-200 rounded p-3 mb-3">
              <p className="text-xs text-violet-800">
                <span className="font-semibold">Emotional impact: </span>
                {lastSentenceData.emotionalResonance}
              </p>
            </div>
          )}

          {/* Lasting Image */}
          {lastSentenceData.lastingImage && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-3">
              <p className="text-xs text-amber-800">
                <span className="font-semibold">What admissions will remember: </span>
                {lastSentenceData.lastingImage}
              </p>
            </div>
          )}

          {/* Characteristics */}
          {lastSentenceData.characteristics && (
            <div className="flex flex-wrap gap-2 mb-3">
              {lastSentenceData.characteristics.showsGrowth && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Shows Growth
                </span>
              )}
              {lastSentenceData.characteristics.hasSpecificity && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Specific Details
                </span>
              )}
              {lastSentenceData.characteristics.feelsComplete && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Satisfying Closure
                </span>
              )}
              {lastSentenceData.characteristics.avoidsPreachiness && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-full">
                  ✅ Authentic
                </span>
              )}
              {!lastSentenceData.characteristics.avoidsPreachiness && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                  ⚠️ Feels Preachy
                </span>
              )}
              {!lastSentenceData.characteristics.feelsComplete && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                  ⚠️ Feels Incomplete
                </span>
              )}
            </div>
          )}

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 space-y-3 border-t border-violet-200 pt-3">
              {/* Strengths */}
              {lastSentenceData.strengths && lastSentenceData.strengths.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-emerald-700 mb-2">Strengths</h4>
                  <ul className="space-y-1">
                    {lastSentenceData.strengths.map((strength, idx) => (
                      <li key={idx} className="text-xs text-slate-700 flex items-start gap-1">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Areas for Improvement */}
              {lastSentenceData.improvements && lastSentenceData.improvements.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 mb-2">Areas for Improvement</h4>
                  <ul className="space-y-1">
                    {lastSentenceData.improvements.map((improvement, idx) => (
                      <li key={idx} className="text-xs text-slate-700 flex items-start gap-1">
                        <span className="text-amber-600 mt-0.5">⚠</span>
                        <span>{improvement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LastSentenceAnalysis;